import React, { useState, useEffect } from "react";
import { useUnifiedService } from "../../../utilities/contexts/UnifiedServiceContext";
import { ServiceStep } from "../../../utilities/types/unified-services";
import { useAutoScrollToTop } from "../../../utilities/hooks/useScrollToTop";
import { getServiceStepFlow } from "../../../utilities/helpers/serviceStepMapper";

const UnifiedGuideStep: React.FC = () => {
  const { serviceInterface, serviceConfig } = useUnifiedService();
  const { setCurrentStep, setCanProceedToNextStep } = serviceInterface;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  const stepFlow = getServiceStepFlow(serviceConfig);

  const { guide } = serviceConfig;
  const totalSlides = guide.slides.length;

  useAutoScrollToTop(currentSlide);

  useEffect(() => {
    setCanProceedToNextStep(totalSlides === 1 || currentSlide === totalSlides - 1);
  }, [totalSlides, currentSlide]);

  useEffect(() => {
    setImageErrors((prev) => ({ ...prev, [currentSlide]: false }));
  }, [currentSlide]);

  useEffect(() => {
    const handleSlideChange = (event: any) => {
      const { slideIndex } = event.detail;
      setCurrentSlide(slideIndex);
      setCanProceedToNextStep(slideIndex === totalSlides - 1);
    };

    window.addEventListener("guideSlideChange", handleSlideChange);
    return () => {
      window.removeEventListener("guideSlideChange", handleSlideChange);
    };
  }, [totalSlides]);

  useEffect(() => {
    const event = new CustomEvent("guideSlideUpdate", { detail: { slideIndex: currentSlide } });
    window.dispatchEvent(event);
  }, [currentSlide]);

  const handleProceed = () => {
    const nextStep = stepFlow.getNextStep(ServiceStep.GUIDE);
    if (nextStep !== null) {
      setCurrentStep(nextStep);
    }
  };
  const currentSlideData = guide.slides[currentSlide];

  return (
    <div className="w-full min-h-fit guide-step">
      <div className="flex justify-center p-4">
        <div className={`flex flex-col gap-4 items-center md:flex-row md:items-start max-w-[40rem]`}>
          {currentSlideData.image && !imageErrors[currentSlide] && (
            <div className="order-2 w-64 min-w-64 md:order-1">
              <div className="flex items-center justify-center p-4 bg-gray-50 rounded-xl aspect-square">
                <img
                  src={currentSlideData.image}
                  alt={currentSlideData.title}
                  className="object-contain max-w-full rounded-lg max-h-fit"
                  onError={() => {
                    setImageErrors((prev) => ({ ...prev, [currentSlide]: true }));
                  }}
                />
              </div>
            </div>
          )}

          <div className={`space-y-4 ${currentSlideData.image && !imageErrors[currentSlide] ? "order-1 md:order-2" : "text-center"}`}>
            <div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">{currentSlideData.title}</h3>
              <div className="m-0">
                <p className="mb-3 leading-relaxed text-gray-600">{currentSlideData.description}</p>
                {currentSlideData.list && currentSlideData.list.length > 0 && (
                  <ul className="space-y-1 text-xs text-left text-gray-600 list-disc list-inside">
                    {currentSlideData.list.map((item, index) => (
                      <li key={index} className="leading-relaxed">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center">
        <button onClick={handleProceed} className="text-sm text-gray-500 transition-colors hover:text-primary">
          Skip guide and start immediately
        </button>
      </div>
    </div>
  );
};

export default UnifiedGuideStep;
